/**
 * Where to take someone once they have logged in.
 *
 * It travels through WorkOS as the sign-in `state`, which is plain text in a
 * URL anyone can edit, so it is checked again on the way back: a path on this
 * site and nothing else, and an arrival state built only from values this file
 * knows. See `RedirectParams` in @workos-inc/authkit-js.
 *
 * Held in memory between AuthKit's callback and the callback page rather than
 * in storage: AuthKit calls back before the router has anything to say, and a
 * full page load in between would lose a session held in memory anyway.
 */

/** Router state a page can arrive with. */
export interface Arrival {
  /** Open on My Schedule rather than Explore. */
  view?: 'schedule';
  /** Reopen one of Share My Schedule's dialogs. */
  share?: 'link' | 'calendar';
}

export interface ReturnTo {
  /** A path on this site, with its query. */
  path: string;
  arrival?: Arrival;
}

export const HOME: ReturnTo = { path: '/' };

/** Pages that would send the user straight back round the login loop. */
const LOOPS = ['/account/login', '/account/signup', '/account/callback'];

function safePath(value: unknown): string | null {
  if (typeof value !== 'string' || value === '') return null;
  let url: URL;
  try {
    url = new URL(value, window.location.origin);
  } catch {
    return null;
  }
  if (url.origin !== window.location.origin) return null;
  // A pathname of "//host" is read as another site by anything that
  // resolves it again.
  if (!url.pathname.startsWith('/') || url.pathname.startsWith('//')) return null;
  if (LOOPS.some((loop) => url.pathname === loop || url.pathname.startsWith(`${loop}/`))) return null;
  return `${url.pathname}${url.search}${url.hash}`;
}

function safeArrival(value: unknown): Arrival | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const raw = value as Record<string, unknown>;
  const arrival: Arrival = {};
  if (raw.view === 'schedule') arrival.view = 'schedule';
  if (raw.share === 'link' || raw.share === 'calendar') arrival.share = raw.share;
  return arrival.view || arrival.share ? arrival : undefined;
}

/** Anything claiming to be a ReturnTo, checked. Null when it isn't one. */
export function parseReturnTo(value: unknown): ReturnTo | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const path = safePath(raw.path);
  if (!path) return null;
  const arrival = safeArrival(raw.arrival);
  return arrival ? { path, arrival } : { path };
}

/** The ReturnTo in a page's router state, if it was sent with one. */
export function returnToFrom(routerState: unknown): ReturnTo | null {
  if (!routerState || typeof routerState !== 'object') return null;
  return parseReturnTo((routerState as { returnTo?: unknown }).returnTo);
}

/** The arrival state a page was opened with, if any. */
export function arrivalFrom(routerState: unknown): Arrival | undefined {
  return safeArrival(routerState);
}

let pending: ReturnTo | null = null;

/** AuthKit's `onRedirectCallback`: keeps the checked destination for the callback page. */
export function rememberReturnTo(state: unknown): void {
  pending = state && typeof state === 'object' ? parseReturnTo((state as { returnTo?: unknown }).returnTo) : null;
}

/** The destination AuthKit brought back, once. */
export function takeReturnTo(): ReturnTo | null {
  const found = pending;
  pending = null;
  return found;
}
